import { RouteList, routesNO, routesEN } from 'api/routes';
import { Button } from 'components/base';
import { Container } from 'components/container';
import { Layout } from 'components/layout';
import { SEO } from 'components/seo';
import { useWindowSize } from 'hooks';
import React, { useState } from 'react';
import styles from './landing-page.module.scss';
import { useSelector } from 'react-redux';
import { authenticatedSelector } from 'store-modules/auth';
import { Language } from '_types/language';
import { languageSelector } from 'store-modules/app-settings';
import {
    returnCopyForLanguage,
    returnRoutesForLanguage,
} from 'services/language-service';
import { useNavigate } from 'react-router-dom';
import Carousel from 'components/carousel/carousel';
import { GoogleLoginButton } from 'components/google-login-button';
import { AppleLoginButton } from 'components/apple-login-button';
import { Loading } from 'components/loading';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const preventDefault = (e: React.MouseEvent<any>): void => e.preventDefault();
interface ViewCopy {
    pageTitle: string;
    title: string;
    title2: string;
    buyPremiumButtonLabel: string;
    buyProButtonLabel: string;
    proCodeLabel: string;
    proTitle: string;
    proTitle2: string;
    divider: string;
    signInLabel: string;
    googleSignUp: string;
    commonSignUp: string;
    appleLoginTitle: string;
}

const nbCopy: ViewCopy = {
    pageTitle: 'Fuelbox',
    title: 'Opprett en konto',
    title2: 'for å komme i gang',
    buyPremiumButtonLabel: 'Kjøp abonnement',
    buyProButtonLabel: 'Konto for jobb/skole',
    proCodeLabel: 'Jeg har en bedriftskode',
    proTitle: 'FuelBox your',
    proTitle2: 'professional life',
    divider: 'Har du allerede en konto?',
    signInLabel: 'Logg inn',
    googleSignUp: 'Fortsett med Google',
    commonSignUp: 'Fortsett med Epost',
    appleLoginTitle: 'Fortsett med Apple',
};

const enCopy: ViewCopy = {
    pageTitle: '',
    title: 'Create an account',
    title2: 'to get started',
    buyPremiumButtonLabel: 'Get subscription',
    buyProButtonLabel: 'Work/school account',
    proCodeLabel: 'I have a Company code',
    proTitle: 'FuelBox your',
    proTitle2: 'professional life',
    divider: 'Already have an account?',
    signInLabel: 'Sign in',
    googleSignUp: 'Continue with Google',
    commonSignUp: 'Continue with Email',
    appleLoginTitle: 'Continue with Apple',
};

const copies: { name: Language; copy: ViewCopy }[] = [
    {
        name: 'nb',
        copy: nbCopy,
    },
    {
        name: 'en',
        copy: enCopy,
    },
];

const routeObj: { name: Language; routes: RouteList }[] = [
    {
        name: 'nb',
        routes: routesNO,
    },
    {
        name: 'en',
        routes: routesEN,
    },
];

const LandingPage: React.FC = () => {
    const navigate = useNavigate();
    const language: Language = useSelector(languageSelector);
    const {
        pageTitle,
        title,
        title2,
        divider,
        signInLabel,
        googleSignUp,
        appleLoginTitle,
        commonSignUp,
    } = returnCopyForLanguage(language, copies);
    const routes = returnRoutesForLanguage(language, routeObj);
    const { view } = useWindowSize(window);
    const authenticated = useSelector(authenticatedSelector);
    const isMobile = view === 'mobile';
    const [loading, setLoading] = useState<boolean>(false);
    const titleBreak = isMobile ? <br /> : '\u00A0';

    const navigateToRegistrationPage =
        (
            authenticated: boolean,
            routes: RouteList,
            navigateToEmailSignup: boolean
        ) =>
        (): void => {
            navigate(routes.createPremium, {
                state: { authenticated, navigateToEmailSignup },
            });
        };

    return loading ? (
        <Loading language={'nb'} />
    ) : (
        <Layout
            routes={routes}
            language={language}
            pageClassName={styles.landingPage}
            headerProps={{
                withLogin: true,
                showLogoLink: true,
                isLandingPage: true,
            }}
            withFooter={true}
            withHeaderSpace={false}
        >
            <SEO title={pageTitle} />
            <div className={styles.bg} />
            <section className={styles.carouselSection}>
                <Carousel />
            </section>
            <section className={styles.welcomeSection}>
                <Container
                    className={styles.welcomeSectionContainer}
                    size='small'
                >
                    <div className={styles.title}>
                        <h1>
                            {title}
                            {titleBreak}
                            {title2}
                        </h1>
                    </div>
                    <div className={styles.loginButtonGroup}>
                        <div className={styles.ButtonsWrapper}>
                            <AppleLoginButton
                                text={appleLoginTitle}
                                routes={routes}
                                setLoading={setLoading}
                                createUser={true}
                            />
                            <GoogleLoginButton
                                text={googleSignUp}
                                routes={routes}
                                setLoading={setLoading}
                                createUser={true}
                                afterOnlyLogin={() => console.log('')}
                            />
                            <Button
                                className={styles.emailCreateButton}
                                leftIcon={'mail_outlined'}
                                onClick={navigateToRegistrationPage(
                                    authenticated,
                                    routes,
                                    true
                                )}
                            >
                                <span>{commonSignUp}</span>
                            </Button>
                        </div>
                        <div className={styles.LoginDivider}>
                            <hr className={styles.LeftLine} />
                            <span className={styles.LoginDividerText}>
                                {divider}
                            </span>
                            <hr className={styles.RightLine} />
                        </div>
                        <Button
                            appearance='cta'
                            style={{
                                minWidth: isMobile ? '260px' : '300px',
                                color: 'white',
                            }}
                            onClick={() => navigate(routes.login)}
                            onMouseDown={preventDefault}
                        >
                            {signInLabel}
                        </Button>
                    </div>
                </Container>
            </section>
        </Layout>
    );
};

export default LandingPage;
